/* --- js/deferred/validated-text-area.js --- */
class ValidatedTextarea extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const textarea = this.querySelector("textarea");
    if (!textarea) throw Error("No textarea found");
    const count = this.querySelector('[data-name="char-count"]');
    if (!count) throw Error("No char count element found");

    textarea.addEventListener("input", () => {
      this.sanitizeTextareaInput(textarea);
      this.updateCharCount(textarea, count);
    });
  }

  /**
   * Sanitizes the input value of a textarea to only allow a-z, 0-9, _, -, :, space (case insensitive).
   * @param {HTMLTextAreaElement} textarea - The textarea element to sanitize.
   */
  sanitizeTextareaInput(textarea) {
    let value = textarea.value;
    // Remove any character that is NOT allowed
    value = value.replace(/[^a-z0-9_\-: !@$#%^&();\[\]\,?\.\'\"]/gi, "");
    // Optional: normalize multiple spaces
    value = value.replace(/\s+/g, " ");
    textarea.value = value;
  }

  /**
   * Updates the character count of a textarea.
   * @param {HTMLTextAreaElement} textarea - The textarea element to update.
   * @param {Element} charCountElement - The element to display the character count.
   */
  updateCharCount(textarea, charCountElement) {
    const length = textarea.value.length;
    const max = textarea.maxLength;
    charCountElement.textContent = `${length}/${max}`;
  }
}
customElements.define("validated-textarea", ValidatedTextarea);


/* --- js/deferred/autoplay-video.js --- */
class AutoplayVideo extends HTMLElement {
  #observer = /** @type {IntersectionObserver | null} */ (null);
  #video = /** @type {HTMLVideoElement | null} */ (null);
  #source = /** @type {HTMLSourceElement | null} */ (null);

  constructor() {
    super();
    this.#observer = null;
    this.#video = /** @type {HTMLVideoElement} */ (this.throw.element("video"));
    this.#source = /** @type {HTMLSourceElement} */ (
      this.throw.element("source")
    );
  }

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#loadVideo();
            this.disconnectedCallback();
          }
        });
      },
      {
        root: null,
        threshold: 0.6,
      },
    );

    /** @ts-ignore */
    this.#observer.observe(this.#video);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #loadVideo() {
    const src = this.#source?.getAttribute("data-src");
    if (!src) throw Error("[autoplay-video]: Video source not found");

    const type = this.#source?.getAttribute("data-video-type");
    if (!type) throw Error("[autoplay-video]: Video type not found");

    this.#source?.setAttribute("src", src);
    this.#source?.setAttribute("type", type);
    this.#video?.setAttribute("preload", "auto");
    this.#video?.load();

    this.#video?.addEventListener(
      "canplaythrough",
      () => {
        this.#video?.play();
      },
      { once: true },
    );
  }
}

customElements.define("autoplay-video", AutoplayVideo);


/* --- js/deferred/video-on-demand.js --- */
class VideoOnDemand extends HTMLElement {
  static _VIDEOPLAYING = "video-playing";
  #observer = /** @type {IntersectionObserver | null} */ (null);
  #isMobile = false;
  #posterMobile;
  #poster;
  #video;
  #videoSource;

  constructor() {
    super();
    this.#isMobile = !window.matchMedia("(min-width: 1024px)").matches;
    this.#posterMobile = /** @type {HTMLImageElement} */ (
      this.throw.element("img.lg\\:hidden")
    );
    this.#poster = /** @type {HTMLImageElement} */ (
      this.throw.element("img.lg\\:block")
    );
    this.#video = /** @type {HTMLVideoElement} */ (this.throw.element("video"));
    this.#videoSource = /** @type {HTMLSourceElement} */ (
      this.throw.element("source")
    );
  }

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handlePosterLoad();
            this.#observer?.unobserve(this);
          }
        });
      },
      {
        rootMargin: "50px",
        threshold: 0.1,
      },
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
    }
  }

  /**
   * Wait for the poster and page to load before loading the video.
   */
  handlePosterLoad() {
    const img = this.#isMobile ? this.#posterMobile : this.#poster;

    const imageReady = img.complete
      ? Promise.resolve()
      : new Promise((resolve) =>
          img.addEventListener("load", resolve, { once: true }),
        );

    const pageReady =
      document.readyState === "complete"
        ? Promise.resolve()
        : new Promise((resolve) =>
            window.addEventListener("load", resolve, { once: true }),
          );

    img.addEventListener(
      "error",
      () => console.warn("Poster failed to load:", img.src),
      { once: true },
    );

    Promise.all([imageReady, pageReady]).then(() => this.loadVideo());
  }

  /**
   * Loads the video source, plays the video, and toggles visibility classes
   */
  loadVideo() {
    const img = this.#isMobile ? this.#posterMobile : this.#poster;

    const src = this.#isMobile
      ? this.#videoSource.getAttribute("data-src-mobile")
      : this.#videoSource.getAttribute("data-src");
    if (!src) throw Error("Video source not found");

    const type = this.#isMobile
      ? this.#videoSource.getAttribute("data-video-type-mobile")
      : this.#videoSource.getAttribute("data-video-type");
    if (!type) throw Error("Video type not found");

    this.#videoSource.src = src;
    this.#videoSource.type = type;
    this.#video.preload = "auto";
    this.#video.load();

    this.#video.addEventListener(
      "canplaythrough",
      () => {
        img.classList.add("opacity-0");
        this.#video.classList.remove("hidden");
        this.#video.play();
        this.#video.dispatchEvent(
          new Event(VideoOnDemand._VIDEOPLAYING, { bubbles: true }),
        );
      },
      { once: true },
    );
  }
}

customElements.define("video-on-demand", VideoOnDemand);


/* --- js/deferred/scroll-indicator.js --- */
const _SCROLL_SNAP_TARGET = "data-snap-target";
const _SCROLL_INDICATORS = "data-indicators";
const _SCROLL_HIDE = "data-hideable";
const _SCROLL_HIDE_ATTR = "data-hidden";
const _SCROLL_LEFT = "data-left";
const _SCROLL_RIGHT = "data-right";
const _SCROLL_SELECTED = "data-selected";
const _SCROLL_REFRESH = "data-refresh";

class ScrollIndicator extends HTMLElement {
  #items = /** @type {HTMLElement[]} */ ([]);
  #indicators = /** @type {HTMLElement[]} */ ([]);
  #activeIndex = 0;
  #intersectionObserver = /** @type {IntersectionObserver | null} */ (null);
  #intersectionRatios = new Map();
  #resizeObserver = /** @type {ResizeObserver | null} */ (null);
  #boundApplyScroll = /** @type {(() => void) | null} */ (null);

  static get observedAttributes() {
    return [_SCROLL_REFRESH];
  }

  /** Reinitializes the scroll indicator when the refresh attribute changes.
   *
   * @param {string} name - The name of the attribute that changed.
   * @param {string | null} oldValue - The old value of the attribute.
   * @param {string | null} newValue - The new value of the attribute.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === _SCROLL_REFRESH && oldValue !== newValue) {
      setTimeout(() => {
        this.disconnectedCallback();
        this.connectedCallback();
      }, 100);
    }
  }

  connectedCallback() {
    this.#newIndicators();
    this.#swipeListeners();
    this.#initHideScroll();
    this.#buttonListeners();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    if (this.#boundApplyScroll) {
      window.removeEventListener("resize", this.#boundApplyScroll);
      window.removeEventListener("load", this.#boundApplyScroll);
    }
    // Reset items/indicators so connectedCallback starts clean
    this.#items = [];
    this.#indicators = [];
    this.#activeIndex = 0;
    this.#intersectionRatios = new Map();
  }

  #newIndicators() {
    const snapTarget = this.getAttribute(_SCROLL_SNAP_TARGET);
    if (!snapTarget) {
      throw Error(
        `[scroll-indicator]: ${_SCROLL_SNAP_TARGET} attribute is required`,
      );
    }
    this.#items = Array.from(this.querySelectorAll(snapTarget));
    if (this.#items.length === 0) {
      throw Error("[scroll-indicator]: no snap target items found");
    }

    const indicatorSelector = this.getAttribute(_SCROLL_INDICATORS);
    if (!indicatorSelector) {
      throw Error(
        `[scroll-indicator]: ${_SCROLL_INDICATORS} attribute is required`,
      );
    }

    const baseIndicator = this.throw.element(indicatorSelector);
    const parent = baseIndicator.parentElement;
    if (!parent) {
      throw Error("[scroll-indicator]: scroll indicators need a parent");
    }

    // Remove all existing indicators and rebuild from scratch
    parent.querySelectorAll(indicatorSelector).forEach((el) => el.remove());

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < this.#items.length; i++) {
      fragment.appendChild(baseIndicator.cloneNode(true));
    }
    parent.appendChild(fragment);

    this.#indicators = Array.from(parent.querySelectorAll(indicatorSelector));
    this.#setActive(0);
  }

  #swipeListeners() {
    if (window.matchMedia("(min-width: 1023px)").matches) return;

    const parent = this.#items[0]?.closest(".overflow-x-auto");
    if (!parent) {
      throw Error("[scroll-indicator]: snap targets need a parent");
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#intersectionRatios.set(entry.target, entry.intersectionRatio);
        }

        // Find the best across ALL items, not just changed ones
        let bestIndex = -1;
        let bestRatio = 0;
        this.#items.forEach((item, index) => {
          const ratio = this.#intersectionRatios.get(item) ?? 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });

        if (bestIndex !== -1) this.#setActive(bestIndex);
      },
      { root: parent, threshold: [0.4, 0.5, 0.6] },
    );

    this.#items.forEach((item) => {
      this.#intersectionRatios.set(item, 0);
      this.#intersectionObserver?.observe(item);
    });
  }

  /**
   * Scrolls to the next item in the given direction.
   * @param {number} index - The current active index.
   * @param {number} direction - The direction to scroll (1 for forward, -1 for backward).
   */
  #scrollBy(index, direction) {
    const itemCount = this.#items.length;
    if (itemCount === 0) {
      throw Error("[scroll-indicator]: #items needs at least one item");
    }
    const nextIndex =
      (((index + direction) % itemCount) + itemCount) % itemCount;
    this.#items[nextIndex].scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
    this.#setActive(nextIndex);
  }

  #buttonListeners() {
    const leftAttribute = this.getAttribute(_SCROLL_LEFT);
    const rightAttribute = this.getAttribute(_SCROLL_RIGHT);
    if (!leftAttribute || !rightAttribute) return;

    this.throw
      .element(leftAttribute)
      .addEventListener("click", () => this.#scrollBy(this.#activeIndex, -1));
    this.throw
      .element(rightAttribute)
      .addEventListener("click", () => this.#scrollBy(this.#activeIndex, 1));
  }

  #initHideScroll() {
    const scrollArea = /** @type {HTMLElement} */ (
      this.#items[0].closest(".overflow-x-auto")
    );
    if (!scrollArea) {
      throw Error(
        `[scroll-indicator]: snap targets need a parent with 'overflow-x-auto'`,
      );
    }
    const hideAttribute = this.getAttribute(_SCROLL_HIDE);
    if (!hideAttribute) {
      return;
    }
    const hideable = this.throw.any(hideAttribute);

    this.#boundApplyScroll = () => {
      const noScroll = scrollArea.scrollWidth <= scrollArea.clientWidth;
      hideable.forEach((element) =>
        element.setAttribute(_SCROLL_HIDE_ATTR, String(noScroll)),
      );
    };

    this.#resizeObserver = new ResizeObserver(this.#boundApplyScroll);
    this.#resizeObserver.observe(scrollArea);
  }

  /** Sets the active tab button based on the given index
   *
   * @param {number} index - The index of the active tab
   */
  #setActive(index) {
    this.#indicators[this.#activeIndex].removeAttribute(_SCROLL_SELECTED);
    this.#indicators[index].setAttribute(_SCROLL_SELECTED, "");
    this.#activeIndex = index;
  }
}

customElements.define("scroll-indicator", ScrollIndicator);


/* --- js/deferred/zapiet.js --- */
class ZapietDeliveryWarner extends HTMLElement {
  constructor() {
    super();
    window.ZapietEvent.listen("locationSelected", this.onLocation.bind(this));
  }

  onLocation(params) {
    const show =
      params.method == "delivery" &&
      params.location.company_name != "West Footscray";
    this.toggleAttribute("data-show", show);
  }
}

customElements.define("zapiet-delivery-warning", ZapietDeliveryWarner);


/* --- js/deferred/sticky-header.js --- */
class StickyHeader extends HTMLElement {
  #isSticky = signal(false);

  constructor() {
    super();
    const sentinel = document.getElementById("header-sentinel");
    if (sentinel) {
      this.init(sentinel);
    }
  }

  /** Initializes the sticky header with the given sentinel element.
   *  @param {HTMLElement} sentinel - The sentinel element to observe. */
  init(sentinel) {
    new IntersectionObserver(([entry]) => {
      this.#isSticky.value = !entry.isIntersecting;
    }).observe(sentinel);

    effect(() => {
      if (this.#isSticky.value) {
        this.setAttribute("data-sticky", "");
      } else {
        this.removeAttribute("data-sticky");
      }
    });
  }
}

customElements.define("sticky-header", StickyHeader);


/* --- js/deferred/front-chat-init.js --- */
(function () {
  var loaded = false;

  function loadFrontChat() {
    if (loaded) return;
    loaded = true;
    cleanup();

    var script = document.createElement("script");
    script.src = "https://chat-assets.frontapp.com/v1/chat.bundle.js";
    script.onload = function () {
      window.FrontChat("init", {
        chatId: "b41bb2f7aa9f770936606bfbcf32c27f",
        useDefaultLauncher: true,
      });
    };
    document.body.appendChild(script);
  }

  var events = ["scroll", "click", "touchstart", "keydown"];

  function cleanup() {
    events.forEach(function (evt) {
      window.removeEventListener(evt, loadFrontChat);
    });
  }

  events.forEach(function (evt) {
    window.addEventListener(evt, loadFrontChat, { once: true, passive: true });
  });
})();


/* --- js/deferred/atl.js --- */
class ATL extends HTMLElement {
  connectedCallback() {
    /** @ts-ignore */
    const zapiet = window.ZapietEvent;
    zapiet.listen("delivery.datepicker.rendered", () => {
      this.toggleAttribute("data-delivery", true);
    });
    zapiet.listen("shipping.datepicker.rendered", () => {
      this.toggleAttribute("data-delivery", true);
    });
    zapiet.listen("pickup.datepicker.rendered", () => {
      this.toggleAttribute("data-delivery", false);
    });
  }
}

customElements.define("authority-to-leave", ATL);


/* --- js/deferred/checkout-button.js --- */
class CheckoutButton extends HTMLElement {
  /** @type {HTMLButtonElement} */
  #button;
  #atl;
  #isZapietOk;
  #hxInFlight = false;

  constructor() {
    super();
    this.#button = /** @type {HTMLButtonElement} */ (
      this.throw.element("button")
    );
    this.#atl = document.body.throw.element("authority-to-leave");
    this.#isZapietOk = Zapiet.Widget.checkoutEnabled() ?? false;
  }

  connectedCallback() {
    this.#button.addEventListener("click", this.#handleClick);

    /** @ts-ignore */
    const zapiet = window.ZapietEvent;
    zapiet.listen("checkoutEnabled", () => {
      this.#isZapietOk = true;
      this.#evaluate();
    });
    zapiet.listen("checkoutDisabled", () => {
      this.#isZapietOk = false;
      this.#evaluate();
    });

    document.body.addEventListener("hx:before-swap", () => {
      this.#hxInFlight = true;
      this.#evaluate();
    });

    document.body.addEventListener("hx:after-swap", () => {
      this.#hxInFlight = false;
      this.#evaluate();
    });

    this.#evaluate();
  }

  get #isDelivery() {
    return this.#atl.getAttribute("data-delivery") != null;
  }

  get atlAnswered() {
    return !!this.#atl.querySelector("input:checked");
  }

  get #canCheckout() {
    if (!this.#isZapietOk) return false;
    if (this.#isDelivery && !this.atlAnswered) return false;
    if (this.#hxInFlight) return false;
    return true;
  }

  #evaluate() {
    this.#button.disabled = !this.#canCheckout;
  }

  #handleClick = () => {
    const href = this.throw
      .element("[data-name='hijacker']")
      .getAttribute("href");
    if (!href) throw new Error("checkout-button: missing href attribute");
    window.location.href = href;
  };
}

customElements.define("checkout-button", CheckoutButton);
